import React from 'react';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { WorkflowEvent } from '@/utils/AuditTrail/types';
import { EVENT_CONFIG } from '@/utils/AuditTrail/constants';
import { DiffTable } from './DiffTable';

export const EventTimeline: React.FC<{ events: WorkflowEvent[]; isAutoApproved?: boolean; autoApprovedDate?: Date }> = ({
  events,
  isAutoApproved,
  autoApprovedDate,
}) => {
  if (isAutoApproved) {
    return (
      <div className="space-y-3">
        <div className="rounded-lg border bg-neutral-50 dark:bg-neutral-800/50 p-4 flex items-start gap-3">
          <div className="mt-0.5 flex-shrink-0">
            <Zap className="h-4 w-4 text-neutral-500" />
          </div>
          <div>
            <div className="font-medium text-sm text-neutral-700 dark:text-neutral-300">
              No Approval Required
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Auto-approved on {autoApprovedDate ? format(autoApprovedDate, 'dd MMM yyyy') : '—'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {events.map((event, index) => {
        const cfg = EVENT_CONFIG[event.type];
        const Icon = cfg.icon;
        const isLast = index === events.length - 1;

        return (
          <div key={event.id} className="flex gap-3">
            {/* Timeline stem */}
            <div className="flex flex-col items-center flex-shrink-0">
              <div className={cn('rounded-full p-1.5 mt-0.5', cfg.className)}>
                <Icon className="h-3 w-3" />
              </div>
              {!isLast && <div className="w-0.5 flex-1 bg-border my-1" />}
            </div>

            {/* Event card */}
            <div className={cn('pb-4 flex-1', isLast ? '' : '')}>
              <div className="rounded-lg border bg-background p-3 space-y-2">
                {/* Header row */}
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <Badge className={cn('text-xs', cfg.className)}>{cfg.label}</Badge>
                  <span className="text-xs text-muted-foreground flex-shrink-0">
                    {format(event.timestamp, 'dd MMM yyyy HH:mm')}
                  </span>
                </div>

                {/* Actor */}
                <div className="text-sm">
                  {event.delegateName && event.originalApproverName ? (
                    <span>
                      Actioned by{' '}
                      <span className="font-medium">{event.delegateName}</span>
                      {' '}on behalf of{' '}
                      <span className="font-medium">{event.originalApproverName}</span>
                    </span>
                  ) : (
                    <span>
                      By <span className="font-medium">{event.actorName}</span>
                    </span>
                  )}
                </div>

                {/* Reason / comment */}
                {event.reason && (
                  <blockquote className="border-l-2 border-muted pl-3 text-sm text-muted-foreground italic">
                    "{event.reason}"
                  </blockquote>
                )}

                {/* Diff table for approver_edited */}
                {event.type === 'approver_edited' && event.diff && event.diff.length > 0 && (
                  <DiffTable diff={event.diff} />
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
